import { onCleanup } from 'solid-js'
import { pasteCopiedNodes, readCopiedNodes } from './clipboard.ts'
import { addLocalAsset } from './localAssets.ts'
import { screenToWorld } from './canvas/camera.ts'
import { clientToWorld, refreshViewportRect } from './canvas/viewportRef.ts'
import { style, viewportSize } from './editor.ts'
import { ensureProject, importProjectFile } from './project.ts'
import { createImage, createText } from './scene/factory.ts'
import { addNodes, scene, select } from './scene/store.ts'
import type { Point, SceneNode } from './scene/types.ts'
import { isTypingTarget } from './util/dom.ts'

/** Fraction of the viewport a pasted image is allowed to fill. */
const FIT_FRACTION = 0.7
/** Offset between images when several arrive at once. */
const CASCADE = 24
/**
 * Linux browsers paste the X11 primary selection when a non-primary button is
 * released; a paste this soon after one is that, not a deliberate paste.
 */
const BUTTON_PASTE_WINDOW_MS = 300

function viewportCenterWorld(): Point {
  const { w, h } = viewportSize()
  return screenToWorld(scene.camera, { x: w / 2, y: h / 2 })
}

/** Natural pixel size of any image the browser can decode, including SVG. */
async function naturalSize(file: Blob): Promise<{ w: number; h: number }> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    img.src = url
    await img.decode()
    // SVGs without intrinsic dimensions report 0; fall back to a sane box.
    return { w: img.naturalWidth || 300, h: img.naturalHeight || 150 }
  } finally {
    URL.revokeObjectURL(url)
  }
}

function fitInView(w: number, h: number): { w: number; h: number } {
  const vs = viewportSize()
  const maxW = (vs.w * FIT_FRACTION) / scene.camera.z
  const maxH = (vs.h * FIT_FRACTION) / scene.camera.z
  const k = Math.min(1, maxW / w, maxH / h)
  return { w: Math.max(1, w * k), h: Math.max(1, h * k) }
}

export async function placeImageFiles(files: File[], at: Point | null): Promise<void> {
  if (files.length === 0) return
  const anchor = at ?? viewportCenterWorld()
  const created: SceneNode[] = []

  for (const [index, file] of files.entries()) {
    try {
      const natural = await naturalSize(file)
      // Assets need a project to live in, so one is created on the first paste.
      const assetId = addLocalAsset(file, ensureProject)
      const fit = fitInView(natural.w, natural.h)
      const shift = index * CASCADE
      created.push(
        createImage(
          { x: anchor.x - fit.w / 2 + shift, y: anchor.y - fit.h / 2 + shift, ...fit },
          assetId,
          natural.w,
          natural.h,
        ),
      )
    } catch (err) {
      console.error('Could not place image', file.name, err)
    }
  }

  if (created.length === 0) return
  addNodes(created, created.length === 1 ? 'Add image' : 'Add images')
  select(created.map((n) => n.id))
}

function placeText(text: string, at: Point | null): void {
  const anchor = at ?? viewportCenterWorld()
  const node = {
    ...createText(anchor, text),
    color: style.textColor,
    fontSize: style.fontSize,
  }
  addNodes([node], 'Paste text')
  select([node.id])
}

export function installPasteAndDrop(): void {
  let auxDown = false
  let lastAuxRelease = -Infinity

  // Pointer events, not mouse events: the viewport cancels pointerdown to pan,
  // which suppresses the compatibility mouseup entirely.
  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) auxDown = true
  }
  const onPointerUp = (e: PointerEvent) => {
    if (e.button === 0 || !auxDown) return
    auxDown = false
    lastAuxRelease = e.timeStamp
    if (!isTypingTarget(e.target as Element | null)) e.preventDefault()
  }

  const onPaste = (e: ClipboardEvent) => {
    if (isTypingTarget(document.activeElement)) return
    if (auxDown || e.timeStamp - lastAuxRelease < BUTTON_PASTE_WINDOW_MS) {
      e.preventDefault()
      return
    }
    const data = e.clipboardData
    if (!data) return

    // Read now: clipboardData is emptied once this handler returns.
    const images = [...data.items]
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null)
    const text = data.getData('text/plain')
    e.preventDefault()

    void (async () => {
      // Our own copy carries layers as well as a PNG; prefer the layers.
      if (images.length > 0) {
        const copied = await readCopiedNodes()
        if (copied) return pasteCopiedNodes(copied)
        return placeImageFiles(images, null)
      }
      if (text.trim() !== '') placeText(text, null)
    })()
  }

  const onDragOver = (e: DragEvent) => {
    // Without this the browser navigates to the dropped file instead.
    e.preventDefault()
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    refreshViewportRect()
    const at = clientToWorld(e)
    const files = [...(e.dataTransfer?.files ?? [])]

    const bundle = files.find((f) => f.name.toLowerCase().endsWith('.lxc'))
    if (bundle) {
      void importProjectFile(bundle).catch((err) => console.error('Import failed', err))
      return
    }

    const images = files.filter((f) => f.type.startsWith('image/'))
    if (images.length > 0) void placeImageFiles(images, at)
  }

  window.addEventListener('pointerdown', onPointerDown, true)
  window.addEventListener('pointerup', onPointerUp, true)
  window.addEventListener('paste', onPaste)
  window.addEventListener('dragover', onDragOver)
  window.addEventListener('drop', onDrop)

  onCleanup(() => {
    window.removeEventListener('pointerdown', onPointerDown, true)
    window.removeEventListener('pointerup', onPointerUp, true)
    window.removeEventListener('paste', onPaste)
    window.removeEventListener('dragover', onDragOver)
    window.removeEventListener('drop', onDrop)
  })
}
